import { useEffect, useRef, useState, type JSX } from 'react';
import confetti from 'canvas-confetti';
import { RemoveScroll } from 'react-remove-scroll';
import { ASSET_CATALOG } from '../assets/catalog';
import {
  deleteExample,
  FOR_MAXIM_SECTION,
  getSoundConfig,
  getSprintConfig,
  isImageFileName,
  isVideoFileName,
  listDesigners,
  listExamples,
  listOverrides,
  replaceExampleImage,
  uploadExample,
  type ExampleRecord,
  type OverrideRecord,
} from './api';
import { AssetPickerModal } from './AssetPickerModal';
import { startCardDrag } from './dragDrop';
import { playDoneChime, setChimeVolume } from './sound';
import { ConfirmDialog } from './ConfirmDialog';
import { Lightbox } from './Lightbox';
import { DEFAULT_EPOCH_MONDAY_UTC, sprintIndexFromMs, sprintLabel } from './sprints';
import {
  createTicket,
  deleteTicket,
  listTickets,
  updateTicket,
  EXAMPLE_ASSET_PREFIX,
  MAX_ASSETS_PER_TICKET,
  TICKET_STATUSES,
  TICKET_STATUS_LABEL,
  type Ticket,
  type TicketStatus,
} from './ticketsApi';

// Position key inside a column group: manual `order` when set, otherwise
// -createdAtMs (newest first). ORDER_STEP pads drops at the group edges.
const ORDER_STEP = 1000;
const posKey = (t: Ticket): number => t.order ?? -(t.createdAtMs ?? 0);

// Tickets board: a compact toolbar (add / assignee filter / sprint filter) over
// three status columns. A sprint is one week; tickets are bucketed by creation
// week, so only the current sprint shows until an older one is picked.
export function TicketsView({
  userEmail,
  isAdmin,
}: {
  userEmail: string;
  isAdmin: boolean;
}): JSX.Element {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [overrides, setOverrides] = useState<Map<string, OverrideRecord>>(new Map());
  const [examples, setExamples] = useState<ExampleRecord[]>([]);
  const [designers, setDesigners] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState<{ images: string[]; index: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Ticket | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Ticket | null>(null);
  const [filterAssignee, setFilterAssignee] = useState('');
  const [epochMs, setEpochMs] = useState(DEFAULT_EPOCH_MONDAY_UTC);
  const [epochLoaded, setEpochLoaded] = useState(false);
  const [dragTicket, setDragTicket] = useState<Ticket | null>(null);
  const [dropStatus, setDropStatus] = useState<TicketStatus | null>(null);
  const [celebratingId, setCelebratingId] = useState<string | null>(null);
  const sweepingRef = useRef(false);

  const nowMs = Date.now();
  const currentSprint = sprintIndexFromMs(nowMs, epochMs);
  const [selectedSprint, setSelectedSprint] = useState(currentSprint);

  const refresh = (): void => {
    listTickets()
      .then((t) => {
        setTickets(t);
        setError('');
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    listExamples()
      .then(setExamples)
      .catch(() => {});
  };

  const doDelete = (): void => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    deleteTicket(id)
      .then(refresh)
      .catch((e) => setError(String(e)));
  };

  useEffect(refresh, []);
  // Overrides resolve a linked asset to its CURRENT (uploaded) image; designers
  // feed the assignee filter / picker.
  useEffect(() => {
    listOverrides()
      .then(setOverrides)
      .catch(() => {});
    listDesigners()
      .then(setDesigners)
      .catch(() => {});
    // Configured sprint epoch (admin settings); realign the default selection to
    // the current sprint under the loaded epoch.
    getSprintConfig()
      .then((c) => {
        const ms = c?.epochMondayMs ?? DEFAULT_EPOCH_MONDAY_UTC;
        setEpochMs(ms);
        setSelectedSprint(sprintIndexFromMs(nowMs, ms));
        setEpochLoaded(true);
      })
      .catch(() => setEpochLoaded(true));
    // Apply the saved chime volume so the Done reward uses the admin setting.
    getSoundConfig()
      .then((c) => {
        if (c) setChimeVolume(c.chimeVolume);
      })
      .catch(() => {});
  }, []);

  const assetThumb = (assetKey: string): { url: string; isImage: boolean; label: string } => {
    // example:<id> links a freeform upload, not a catalog asset
    if (assetKey.startsWith(EXAMPLE_ASSET_PREFIX)) {
      const id = assetKey.slice(EXAMPLE_ASSET_PREFIX.length);
      const ex = examples.find((x) => x.id === id);
      return { url: ex?.url ?? '', isImage: true, label: ex?.name ?? 'missing' };
    }
    const entry = ASSET_CATALOG.find((e) => e.key === assetKey);
    return {
      url: overrides.get(assetKey)?.url ?? assetKey,
      isImage: entry?.kind !== 'audio',
      label: assetKey.split('/').pop() ?? assetKey,
    };
  };

  // For Maxim uploads offered in the sprite picker (images only)
  const forMaximExamples = examples.filter(
    (ex) => ex.section === FOR_MAXIM_SECTION && isImageFileName(ex.name),
  );

  // Open the lightbox on `url` within `images`, so Left/Right step the ticket set.
  const openImages = (images: string[], url: string): void => {
    const at = images.indexOf(url);
    setZoom({ images, index: at >= 0 ? at : 0 });
  };

  const sprintOf = (t: Ticket): number =>
    t.sprintIndex ?? sprintIndexFromMs(t.createdAtMs ?? nowMs, epochMs);

  // sprint is over - unfinished work tickets fall back to the timeless backlog.
  // waits for the configured epoch so we do not sweep with the wrong week math
  useEffect(() => {
    if (loading || !epochLoaded || sweepingRef.current) return;
    const stale = tickets.filter(
      (t) =>
        (t.status === 'todo' || t.status === 'in_progress') && sprintOf(t) < currentSprint,
    );
    if (stale.length === 0) return;
    sweepingRef.current = true;
    Promise.all(stale.map((t) => updateTicket(t.id, { status: 'backlog' })))
      .then(refresh)
      .catch((e) => setError(String(e)))
      .finally(() => {
        sweepingRef.current = false;
      });
  }, [tickets, loading, epochLoaded, epochMs]);

  const moveTicket = (
    t: Ticket,
    next: TicketStatus,
    before: string | null,
    at?: { x: number; y: number },
  ): void => {
    // dropped back onto its own slot
    if (before === t.id) return;
    // backlog and done are not manually sorted - a drop there is only a
    // status change
    if (t.status === next && (next === 'backlog' || next === 'done')) return;
    const update: Parameters<typeof updateTicket>[1] = {};
    if (next !== 'backlog' && next !== 'done') {
      const others = ticketsForColumn(next).filter((x) => x.id !== t.id);
      let slot = before ? others.findIndex((x) => x.id === before) : others.length;
      if (slot < 0) slot = others.length;
      // mine-first grouping wins over the drop point - the new order sits
      // between the nearest neighbors from the ticket's own group
      const inGroup = (x: Ticket): boolean => isMineTicket(x) === isMineTicket(t);
      const prevCard = others.slice(0, slot).reverse().find(inGroup);
      const nextCard = others.slice(slot).find(inGroup);
      if (prevCard && nextCard) update.order = (posKey(prevCard) + posKey(nextCard)) / 2;
      else if (nextCard) update.order = posKey(nextCard) - ORDER_STEP;
      else if (prevCard) update.order = posKey(prevCard) + ORDER_STEP;
      else update.order = 0;
    }
    if (next === 'done') update.doneAtMs = Date.now();
    if (t.status !== next) {
      update.status = next;
      // work columns are sprint-scoped - a moved ticket always joins the current sprint
      if (next !== 'backlog') update.sprintIndex = currentSprint;
    }
    // dropping into Done: confetti at the drop point, delay the refresh so the
    // green pulse plays on the card before it jumps columns
    if (next === 'done' && t.status !== 'done') {
      setCelebratingId(t.id);
      playDoneChime();
      window.setTimeout(() => setCelebratingId(null), 1100);
      confetti({
        particleCount: 90,
        spread: 75,
        startVelocity: 38,
        scalar: 0.9,
        origin: at
          ? { x: at.x / window.innerWidth, y: at.y / window.innerHeight }
          : { x: 0.5, y: 0.5 },
      });
      updateTicket(t.id, update)
        .then(() => window.setTimeout(refresh, 900))
        .catch((e) => setError(String(e)));
      return;
    }
    updateTicket(t.id, update)
      .then(refresh)
      .catch((e) => setError(String(e)));
  };

  const beginCardDrag = (t: Ticket, e: PointerEvent, el: HTMLElement): void => {
    startCardDrag(e, el, {
      onLift: () => setDragTicket(t),
      onHover: (s) => setDropStatus(s as TicketStatus | null),
      onFinish: (s, at, before) => {
        setDragTicket(null);
        setDropStatus(null);
        if (s && (TICKET_STATUSES as readonly string[]).includes(s)) {
          moveTicket(t, s as TicketStatus, before, at);
        }
      },
    });
  };

  const matchesAssignee = (t: Ticket): boolean =>
    filterAssignee === '' || t.assignee === filterAssignee;

  // Sprint dropdown: every sprint that has tickets, plus the current one, newest
  // first. Backlog tickets are sprint-agnostic, so they don't feed the dropdown.
  const sprintSet = new Set<number>(
    tickets.filter((t) => t.status !== 'backlog').map(sprintOf),
  );
  sprintSet.add(currentSprint);
  const sprintOptions = [...sprintSet].sort((a, b) => b - a);

  const isMineTicket = (t: Ticket): boolean =>
    !!t.assignee && t.assignee.toLowerCase() === userEmail.toLowerCase();

  // Backlog ignores the sprint filter (always visible); other columns are
  // scoped to the selected sprint.
  const ticketsForColumn = (status: TicketStatus): Ticket[] =>
    tickets
      .filter(
        (t) =>
          t.status === status &&
          matchesAssignee(t) &&
          (status === 'backlog' || sprintOf(t) === selectedSprint),
      )
      .sort((a, b) => {
        // done autosorts by completion date, newest on top, no grouping
        if (status === 'done')
          return (b.doneAtMs ?? b.createdAtMs ?? 0) - (a.doneAtMs ?? a.createdAtMs ?? 0);
        // my tickets always group above others
        const groupDiff = Number(isMineTicket(b)) - Number(isMineTicket(a));
        if (groupDiff !== 0) return groupDiff;
        // backlog keeps pure creation order - no manual sort there
        if (status === 'backlog') return (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0);
        return posKey(a) - posKey(b);
      });

  // Reference/TODO images uploaded with a ticket, grouped by ticket id.
  const examplesByTicket = new Map<string, ExampleRecord[]>();
  for (const ex of examples) {
    if (!ex.ticketId) continue;
    const list = examplesByTicket.get(ex.ticketId);
    if (list) list.push(ex);
    else examplesByTicket.set(ex.ticketId, [ex]);
  }

  // rough card height for dealing backlog tickets into the shorter column
  const estimateHeight = (t: Ticket): number => {
    const assets = (t.assetKeys?.length ?? 0) + (examplesByTicket.get(t.id)?.length ?? 0);
    let h = 120;
    if (t.description) h += Math.min(t.description.split('\n').length, 10) * 18;
    if (t.assignee) h += 26;
    h += Math.ceil(assets / 2) * 66;
    return h;
  };

  // few done tickets - show them open; a crowded Done column starts collapsed
  const doneExpandedByDefault = ticketsForColumn('done').length < 4;

  if (loading) return <div className="placeholder">Loading tickets...</div>;

  return (
    <div className="tickets">
      {error && <div className="error">{error}</div>}

      <div className="ticket-toolbar">
        <button className="primary" onClick={() => setShowCreate(true)}>
          <i className="fas fa-plus" /> Add ticket
        </button>
        <label className="toolbar-field">
          <i className="fas fa-user" />
          <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
            <option value="">All assignees</option>
            {designers.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar-field">
          <i className="fas fa-calendar-week" />
          <select
            value={selectedSprint}
            onChange={(e) => setSelectedSprint(Number(e.target.value))}
            style={selectedSprint === currentSprint ? { padding: '6px 10px 6px 0px' } : undefined}
          >
            {sprintOptions.map((s) => (
              <option key={s} value={s}>
                {s === currentSprint ? '🎯 ' : ''}
                {sprintLabel(s, epochMs)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="board">
        {TICKET_STATUSES.map((status) => {
          const columnTickets = ticketsForColumn(status);
          const renderCard = (t: Ticket, index: number): JSX.Element => (
            <TicketCard
              key={t.id}
              ticket={t}
              cardIndex={index}
              examples={examplesByTicket.get(t.id) ?? []}
              onOpen={openImages}
              assetThumb={assetThumb}
              isAdmin={isAdmin}
              onDelete={setConfirmDelete}
              onEdit={setEditing}
              userEmail={userEmail}
              defaultExpanded={doneExpandedByDefault}
              celebrating={celebratingId === t.id}
              dragging={dragTicket?.id === t.id}
              onDragStart={beginCardDrag}
            />
          );
          const isDropTarget = dropStatus === status && !!dragTicket;
          return (
            <div
              key={status}
              data-drop-status={status}
              // append: no manual sort, drop = status change only; grouped:
              // slots are picked within the dragged card's mine/other group
              data-drop-mode={
                status === 'backlog' || status === 'done' ? 'append' : 'grouped'
              }
              className={`column ${isDropTarget ? 'drop-target' : ''}`}
            >
              <h3>{TICKET_STATUS_LABEL[status]}</h3>
              {status === 'backlog' ? (
                (() => {
                  // deal cards in order (mine first) into the shorter column -
                  // tight masonry-like stacks with a controlled order
                  const cols: [JSX.Element[], JSX.Element[]] = [[], []];
                  const heights: [number, number] = [0, 0];
                  columnTickets.forEach((t, index) => {
                    const at = heights[0] <= heights[1] ? 0 : 1;
                    cols[at].push(renderCard(t, index));
                    heights[at] += estimateHeight(t);
                  });
                  return (
                    <div className="column-cards backlog">
                      <div className="backlog-col">{cols[0]}</div>
                      <div className="backlog-col">{cols[1]}</div>
                    </div>
                  );
                })()
              ) : (
                <div className="column-cards">{columnTickets.map(renderCard)}</div>
              )}
            </div>
          );
        })}
      </div>

      {showCreate && (
        <CreateTicketModal
          userEmail={userEmail}
          designers={designers}
          assetThumb={assetThumb}
          forMaxim={forMaximExamples}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <CreateTicketModal
          userEmail={userEmail}
          designers={designers}
          assetThumb={assetThumb}
          forMaxim={forMaximExamples}
          ticket={editing}
          isAdmin={isAdmin}
          existingExamples={examplesByTicket.get(editing.id) ?? []}
          onChanged={refresh}
          onClose={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {zoom && (
        <Lightbox
          images={zoom.images}
          index={zoom.index}
          onIndexChange={(next) => setZoom((z) => (z ? { ...z, index: next } : z))}
          onClose={() => setZoom(null)}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete ticket?"
          message={confirmDelete.title}
          confirmLabel="Delete"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

function CreateTicketModal({
  userEmail,
  designers,
  assetThumb,
  forMaxim,
  ticket,
  isAdmin = false,
  existingExamples = [],
  onChanged,
  onClose,
  onCreated,
}: {
  userEmail: string;
  designers: string[];
  assetThumb: (key: string) => { url: string; isImage: boolean; label: string };
  forMaxim: ExampleRecord[];
  // Present = edit an existing ticket; absent = create a new one.
  ticket?: Ticket;
  isAdmin?: boolean;
  // Example/TODO images already uploaded for this ticket (edit mode only).
  existingExamples?: ExampleRecord[];
  // Refresh the board after a replace/delete (without closing the modal).
  onChanged?: () => void;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const isEdit = !!ticket;
  const [title, setTitle] = useState(ticket?.title ?? '');
  const [description, setDescription] = useState(ticket?.description ?? '');
  const [assignee, setAssignee] = useState(ticket?.assignee ?? '');
  const [assetKeys, setAssetKeys] = useState<string[]>(ticket?.assetKeys ?? []);
  const [existing, setExisting] = useState<ExampleRecord[]>(existingExamples);
  const [exampleFiles, setExampleFiles] = useState<File[]>([]);
  const [todoFiles, setTodoFiles] = useState<File[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const replaceExisting = async (ex: ExampleRecord, file: File | null): Promise<void> => {
    if (!file) return;
    setError('');
    try {
      const obj = await replaceExampleImage({ id: ex.id, file, section: ex.section, userEmail });
      setExisting((cur) => cur.map((x) => (x.id === ex.id ? { ...x, ...obj } : x)));
      onChanged?.();
    } catch (e) {
      setError(String(e));
    }
  };

  const deleteExisting = async (ex: ExampleRecord): Promise<void> => {
    setError('');
    try {
      await deleteExample(ex.id);
      setExisting((cur) => cur.filter((x) => x.id !== ex.id));
      onChanged?.();
    } catch (e) {
      setError(String(e));
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const removeAsset = (k: string): void => setAssetKeys((cur) => cur.filter((x) => x !== k));
  const addExamples = (files: FileList | null): void => {
    if (files) setExampleFiles((cur) => [...cur, ...Array.from(files)]);
  };
  const removeExample = (i: number): void =>
    setExampleFiles((cur) => cur.filter((_, idx) => idx !== i));
  const addTodos = (files: FileList | null): void => {
    if (files) setTodoFiles((cur) => [...cur, ...Array.from(files)]);
  };
  const removeTodo = (i: number): void =>
    setTodoFiles((cur) => cur.filter((_, idx) => idx !== i));

  const submit = async (): Promise<void> => {
    if (!title.trim()) return;
    setBusy(true);
    setError('');
    try {
      let ticketId: string;
      if (ticket) {
        await updateTicket(ticket.id, { title: title.trim(), description, assignee, assetKeys });
        ticketId = ticket.id;
      } else {
        ticketId = await createTicket(
          { title: title.trim(), description, assignee, assetKeys },
          userEmail,
        );
      }
      // Upload reference images into the Examples section, linked to the ticket.
      for (const file of exampleFiles) {
        await uploadExample({ file, section: 'Examples', userEmail, ticketId });
      }
      // Upload TODO images into the TODO section, linked to the ticket.
      for (const file of todoFiles) {
        await uploadExample({ file, section: 'TODO', userEmail, ticketId });
      }
      onCreated();
    } catch (e) {
      setError(String(e));
      setBusy(false);
    }
  };

  return (
    <RemoveScroll>
      {/* no close on outside click - half-typed ticket text is too easy to lose */}
      <div className="modal-overlay">
        <div className="modal-card create-ticket" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <strong>{isEdit ? 'Edit ticket' : 'New ticket'}</strong>
            <button className="modal-close" onClick={onClose} title="Close">
              <i className="fas fa-times" />
            </button>
          </div>
          <input
            placeholder="Ticket title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">(unassigned)</option>
            {designers.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <div className="selected-assets">
        {assetKeys.map((k) => {
          const thumb = assetThumb(k);
          return (
            <span key={k} className="selected-asset">
              {thumb.isImage ? (
                <img className="asset-thumb" src={thumb.url} alt="" loading="lazy" />
              ) : (
                <span className="asset-thumb audio">audio</span>
              )}
              <span className="asset-link-label">{thumb.label}</span>
              <button type="button" className="chip-x" onClick={() => removeAsset(k)} title="Remove">
                <i className="fas fa-times" />
              </button>
            </span>
          );
        })}
        <button type="button" className="btn" onClick={() => setShowPicker(true)}>
          <i className="fas fa-plus" /> add sprites ({assetKeys.length}/{MAX_ASSETS_PER_TICKET})
        </button>
      </div>

      {isEdit && existing.length > 0 && (
        <div className="selected-assets">
          {existing.map((ex) => (
            <span key={ex.id} className="selected-asset">
              {isImageFileName(ex.name) ? (
                <img className="asset-thumb" src={ex.url} alt="" loading="lazy" />
              ) : isVideoFileName(ex.name) ? (
                <video className="asset-thumb" src={`${ex.url}#t=0.1`} muted preload="metadata" />
              ) : ex.thumbUrl ? (
                <img className="asset-thumb" src={ex.thumbUrl} alt="" loading="lazy" />
              ) : (
                <span className="asset-thumb file">
                  <i className="fas fa-file" />
                </span>
              )}
              <span className="asset-link-label">{ex.section}</span>
              {isAdmin && (
                <>
                  <label className="chip-x" title="Replace image">
                    <i className="fas fa-upload" />
                    <input
                      type="file"
                      accept="image/*,video/mp4,.mp4"
                      hidden
                      onChange={(e) => replaceExisting(ex, e.target.files?.[0] ?? null)}
                    />
                  </label>
                  <button
                    type="button"
                    className="chip-x"
                    title="Remove image"
                    onClick={() => deleteExisting(ex)}
                  >
                    <i className="fas fa-times" />
                  </button>
                </>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="selected-assets">
        {exampleFiles.map((f, i) => (
          <span key={`${f.name}-${i}`} className="chip">
            {f.name}
            <button type="button" className="chip-x" onClick={() => removeExample(i)} title="Remove">
              <i className="fas fa-times" />
            </button>
          </span>
        ))}
        <label className="btn">
          <i className="fas fa-image" /> add example
          <input
            type="file"
            accept="image/*,video/mp4,.mp4,.psd,.ai,.eps,.pdf"
            multiple
            hidden
            onChange={(e) => addExamples(e.target.files)}
          />
        </label>
      </div>

      <div className="selected-assets">
        {todoFiles.map((f, i) => (
          <span key={`${f.name}-${i}`} className="chip">
            {f.name}
            <button type="button" className="chip-x" onClick={() => removeTodo(i)} title="Remove">
              <i className="fas fa-times" />
            </button>
          </span>
        ))}
        <label className="btn">
          <i className="fas fa-tasks" /> add TODO
          <input
            type="file"
            accept="image/*,video/mp4,.mp4,.psd,.ai,.eps,.pdf"
            multiple
            hidden
            onChange={(e) => addTodos(e.target.files)}
          />
        </label>
      </div>

      <textarea
        placeholder="Description"
        rows={6}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <button className="primary" disabled={busy || !title.trim()} onClick={submit}>
        {busy ? 'Saving...' : isEdit ? 'Save changes' : 'Create ticket'}
      </button>
          {error && <div className="error">{error}</div>}

          {showPicker && (
            <AssetPickerModal
              initial={assetKeys}
              max={MAX_ASSETS_PER_TICKET}
              forMaxim={forMaxim}
              assetThumb={assetThumb}
              onClose={() => setShowPicker(false)}
              onConfirm={(keys) => {
                setAssetKeys(keys);
                setShowPicker(false);
              }}
            />
          )}
        </div>
      </div>
    </RemoveScroll>
  );
}

function TicketCard({
  ticket,
  cardIndex,
  examples,
  onOpen,
  assetThumb,
  isAdmin,
  onDelete,
  onEdit,
  userEmail,
  defaultExpanded,
  celebrating,
  dragging,
  onDragStart,
}: {
  ticket: Ticket;
  // position in the column's ordered list, for drop slot lookup
  cardIndex: number;
  examples: ExampleRecord[];
  onOpen: (images: string[], url: string) => void;
  assetThumb: (assetKey: string) => { url: string; isImage: boolean; label: string };
  isAdmin: boolean;
  onDelete: (ticket: Ticket) => void;
  onEdit: (ticket: Ticket) => void;
  userEmail: string;
  defaultExpanded: boolean;
  celebrating: boolean;
  dragging: boolean;
  onDragStart: (ticket: Ticket, e: PointerEvent, el: HTMLElement) => void;
}): JSX.Element {
  // done cards collapse to the title so the Done column stays short;
  // null = no manual toggle yet, follow the column default
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const expanded = expandedOverride ?? defaultExpanded;
  const assetKeys = ticket.assetKeys ?? [];
  const isMine = !!ticket.assignee && ticket.assignee.toLowerCase() === userEmail.toLowerCase();
  const isDone = ticket.status === 'done';
  const collapsed = isDone && !expanded;

  // Every image in this ticket, in display order (linked assets, then examples) -
  // the set the lightbox arrows step through.
  const ticketImages: string[] = [
    ...assetKeys.map((k) => assetThumb(k)).filter((t) => t.isImage).map((t) => t.url),
    ...examples
      .filter((ex) => isImageFileName(ex.name) || isVideoFileName(ex.name))
      .map((ex) => ex.url),
  ];

  return (
    <div
      className={`ticket-card ${isMine ? 'mine' : ''} ${celebrating ? 'celebrate' : ''} ${collapsed ? 'collapsed' : ''} ${dragging ? 'dragging' : ''}`}
      data-card-id={ticket.id}
      data-card-index={cardIndex}
      data-card-group={isMine ? 'mine' : 'other'}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        // buttons, links and form controls inside the card keep working
        if ((e.target as HTMLElement).closest('button, a, select, input, textarea, label')) return;
        onDragStart(ticket, e.nativeEvent, e.currentTarget);
      }}
      // block native drags (images inside the card are draggable by default)
      onDragStart={(e) => e.preventDefault()}
    >
      {isAdmin && (
        <div className="ticket-tools">
          <button className="ticket-tool" onClick={() => onEdit(ticket)} title="Edit ticket">
            <i className="fas fa-pen" />
          </button>
          <button className="ticket-tool" onClick={() => onDelete(ticket)} title="Delete ticket">
            <i className="fas fa-trash" />
          </button>
        </div>
      )}
      <div
        className={`ticket-title ${isDone ? 'toggle' : ''}`}
        onClick={isDone ? () => setExpandedOverride(!expanded) : undefined}
      >
        {ticket.title}
        {isDone && <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} collapse-chevron`} />}
      </div>
      {!collapsed && ticket.description && <div className="ticket-desc">{ticket.description}</div>}
      {!collapsed &&
        ticket.assignee &&
        (isMine ? (
          <div className="ticket-meta mine">
            <i className="fas fa-user" /> {ticket.assignee}
          </div>
        ) : (
          <div className="ticket-meta muted">{ticket.assignee}</div>
        ))}
      {!collapsed && assetKeys.length > 0 && (
        <div className="asset-links">
          {assetKeys.map((k) => {
            const thumb = assetThumb(k);
            return (
              <button
                key={k}
                type="button"
                className="asset-link"
                title="View linked asset"
                onClick={() => {
                  if (thumb.isImage) onOpen(ticketImages, thumb.url);
                }}
              >
                {thumb.isImage ? (
                  <img className="asset-thumb" src={thumb.url} alt="" loading="lazy" />
                ) : (
                  <span className="asset-thumb audio">audio</span>
                )}
                <span className="asset-link-label">{thumb.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {!collapsed && examples.length > 0 && (
        <div className="asset-links">
          {examples.map((ex) =>
            isImageFileName(ex.name) || isVideoFileName(ex.name) ? (
              <button
                key={ex.id}
                type="button"
                className="asset-link"
                title={`${ex.section}: ${ex.name}`}
                onClick={() => onOpen(ticketImages, ex.url)}
              >
                {isVideoFileName(ex.name) ? (
                  <video className="asset-thumb" src={`${ex.url}#t=0.1`} muted preload="metadata" />
                ) : (
                  <img className="asset-thumb" src={ex.url} alt="" loading="lazy" />
                )}
                <span className="asset-link-label">{ex.section}</span>
              </button>
            ) : (
              <a
                key={ex.id}
                className="asset-link"
                href={ex.url}
                download
                target="_blank"
                rel="noreferrer"
                title={`${ex.section}: ${ex.name}`}
              >
                {ex.thumbUrl ? (
                  <img className="asset-thumb" src={ex.thumbUrl} alt="" loading="lazy" />
                ) : (
                  <span className="asset-thumb file">
                    <i className="fas fa-file" />
                  </span>
                )}
                <span className="asset-link-label">{ex.section}</span>
              </a>
            ),
          )}
        </div>
      )}
    </div>
  );
}

